/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * CreateApplicationUserPage
 *
 * React page responsible for creating new application users with full
 * client-side + server-side validation, progress state, and controlled error handling.
 * Features:
 * - Accepts validated form data from CreateApplicationUserComponent
 * - Sends POST request to backend `/arcee/user/register/`
 * - Handles AbortController for safe cancellation and navigation
 * - Displays ProgressBarDialog during network operations
 * - Inline server validation errors mapped and shown inside the form
 * - AlertDialog for success and non-field / network errors
 * - Redirects back to the application's user list after successful creation
 */

import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import CreateApplicationUserComponent from '../components/applications/CreateApplicationUserComponent';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';
import { retrieve } from "../utils/storageHelper";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

export default function CreateApplicationUserPage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams(); // application id from route (same param name you used for roles)
  
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const controllerRef = useRef(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');

  const [serverErrors, setServerErrors] = useState(null);
  const userData = retrieve('userData');

  // Helper to convert server validation error payload into friendly mapping
  function mapServerErrors(errBody) {
    if (!errBody || typeof errBody !== 'object') return null;
    const mapped = {};
    // map backend keys to component field names
    const keyMap = {
      'first_name': 'firstName',
      'last_name': 'lastName',
      'username': 'username',
      'email': 'email',
      'password': 'password'
    };

    for (const [k, v] of Object.entries(errBody)) {
      const targetKey = keyMap[k] || k;
      if (Array.isArray(v)) mapped[targetKey] = v.join(' ');
      else mapped[targetKey] = String(v);
    }
    return mapped;
  }

  // Called by child component when user submits valid form
  const handleSubmit = async (payload) => {
    // Ensure application id from route is present
    const applicationId = id;
    if (!applicationId) {
      setAlertTitle('Missing Application');
      setAlertMessage('Application id is missing. Please access this page from the application context.');
      setAlertType('error');
      setAlertOpen(true);
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    setProgressMessage('Creating user...');
    setProgressOpen(true);
    setServerErrors(null);

    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    // Build request body (map keys to API expected format)
    const bodyPayload = {
      applications: [Number(applicationId)],
      username: payload.username,
      email: payload.email,
      password: payload.password,
      first_name: payload.firstName || '',
      last_name: payload.lastName || '',
      mail_credentials: !!payload.sendMail
    };

    try {
      const resp = await apiHelper.apiCall('/arcee/user/create/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload),
        signal,
      });

      if (!resp.ok) {
        // parse JSON or fallback to text
        let errBody;
        try {
          errBody = await resp.json();
        } catch (jsonErr) {
          try {
            errBody = await resp.text();
          } catch (textErr) {
            errBody = null;
          }
        }

        console.warn('Create user failed', { status: resp.status, body: errBody });

        // If backend returned field errors (DRF style), show inline
        if (errBody && typeof errBody === 'object' && !Array.isArray(errBody)) {
          const mapped = mapServerErrors(errBody);
          setServerErrors(mapped);
          return;
        }

        // otherwise show AlertDialog
        const message = typeof errBody === 'string' ? errBody : `HTTP ${resp.status}`;
        setAlertTitle('Failed to create user');
        setAlertMessage(message);
        setAlertType('error');
        setAlertOpen(true);
        return;
      }

      // success
      const data = await resp.json().catch(() => ({}));
      setServerErrors(null);
      const successMsg = data.message || 'User created successfully.';
      setAlertTitle('Success');
      setAlertMessage(successMsg);
      setAlertType('success');
      setAlertOpen(true);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('Create user error:', err);
      setAlertTitle('Network Error');
      setAlertMessage(err.message || 'Could not contact server. Please try again later.');
      setAlertType('error');
      setAlertOpen(true);
    } finally {
      setProgressOpen(false);
      setSubmitting(false);
      controllerRef.current = null;
    }
  };

  const handleCancel = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    if (id) {
      navigate(`/dashboard/applications/${id}/users`);
    } else {
      navigate('/dashboard/applications');
    }
    window.location.reload();
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
    if (alertType === 'success') {
      if (id) {
        navigate(`/dashboard/applications/${id}/users`);
        window.location.reload();
      }
    }
  };

  return (
    <>
      <CreateApplicationUserComponent onSubmit={handleSubmit} onCancel={handleCancel} serverErrors={serverErrors} />

      <ProgressBarDialog open={progressOpen} text_message={progressMessage} cancellable={false} />

      <AlertDialog
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        type={alertType}
        okLabel="OK"
        showCancel={false}
        onOk={handleAlertClose}
        onCancel={() => setAlertOpen(false)}
        mini_mode={false}
      />
    </>
  );
}
